import { get, set } from 'lib/storage/versionedLocalStorageUtils'
import type { Dispatch, SetStateAction } from 'react'
import { useCallback, useEffect, useRef, useState } from 'react'
import useIsomorphicLayoutEffect from './useIsomorphicLayoutEffect'

function useLocallyPersistedState<T>(args: {
  defaultValue: T | (() => T)
  key: string
  version?: number
  returnDefaultBeforeRestoring: true
}): [T, Dispatch<SetStateAction<T>>]
function useLocallyPersistedState<T>(args: {
  defaultValue: T | (() => T)
  key: string
  version?: number
  returnDefaultBeforeRestoring: false
}): [T | undefined, Dispatch<SetStateAction<T>>]
function useLocallyPersistedState<T>({
  defaultValue,
  key,
  version = 0,
  returnDefaultBeforeRestoring,
}: {
  defaultValue: T | (() => T)
  key: string
  version?: number
  returnDefaultBeforeRestoring: boolean
}): [T | undefined, Dispatch<SetStateAction<T>>] {
  const [state, setState] = useState(defaultValue)

  const hasRetrievedFromLocalStorage = useRef(false)

  useIsomorphicLayoutEffect(() => {
    /*
     * Ideally we could do this within the useState initialiser,
     * but that would lead to server/client render mismatches.
     */
    const storedValue = get<T>(key, version)
    if (storedValue !== null) {
      setState(storedValue)
    }
    hasRetrievedFromLocalStorage.current = true
  }, [key, version])

  const hasBeenUpdated = useRef(false)

  const setAndStoreState = useCallback((newState: SetStateAction<T>) => {
    setState(newState)
    hasBeenUpdated.current = true
  }, [])

  useEffect(() => {
    // Check `hasBeenUpdated` to avoid writing to localStorage on mount
    if (hasBeenUpdated.current) {
      set(key, version, state)
    }
  }, [key, version, state])

  return [
    (hasRetrievedFromLocalStorage.current || returnDefaultBeforeRestoring) ? state : undefined,
    setAndStoreState,
  ]
}

export default useLocallyPersistedState
